import useApiCall from "../useApiCall";
import constants from "../../constants";
import { getDataFromStorage } from "../../util";
import CodingPageHooks from "./CodingPageHooks";
import CodingTestHooks from "./CodingTestHooks";
import useAxiosPrivate from "../useAxiosPrivate";
import AlertContext from "../../context/AlertContext";
import NotifyBody from "../../Components/Notify/NotifyBody";
import React, { useContext, useRef, useState, useEffect } from "react";
import useCommonFunctionHooks from "../useCommonFunctionHooks";
import MockTestLibraryHooks from "../Mock/Student/MockTestLibraryHooks";
import StudentTestPageHooks from "../StudentTestPage/StudentTestPageHooks";
import McqAndDescPageAnsFnHooks from "../McqAndDescPage/McqAndDescPageAnsFnHooks";
import InstructionPageHooks from "../InstructionPage/InstructionPageHooks";

export default function CodingHeaderLearnerSideHooks() {
  const Alert = useContext(AlertContext);
  const alertRef = useRef(Alert);

  // Keep the ref updated with latest Alert state
  useEffect(() => {
    alertRef.current = Alert;
  }, [Alert]);

  const axios = useAxiosPrivate();
  const { apiCall } = useApiCall();
  const { updateStatus } = CodingTestHooks();
  const { handleCodingInfo } = MockTestLibraryHooks();
  const { handleAssignNextQnToLearner } = CodingPageHooks();
  const { handleShowAnsforPrac } = McqAndDescPageAnsFnHooks();
  const { removeAccess } = InstructionPageHooks();
  
  const {
    handleClose,
    handleNavigate,
    getCatchBlockDetails,
    getHeaderDetailsForAxios,
  } = useCommonFunctionHooks();

  let successfulMsgForLearnerSubmission = [
    "Good, you cleared this assignment.",
    "Awesome! Your code passed our test.",
    "Well done! the code submitted is right.",
    "That's right, your code worked and you cleared this assignment.",
  ];

  let timeoutId = useRef({});
  let switchCount = useRef(0);

  /* handling multiplesubmit */
  const isBlurHandled = useRef(false); // Guard flag

  const focusOutSec = useRef(0);
  const focusOutTimer = useRef(null);

  const [timer, setTimer] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const pad = (num) => {
    return ("0" + num).slice(-2);
  };

  const getSuccessMsg = () => {
    let i = Math.floor(
      Math.random() * successfulMsgForLearnerSubmission.length
    );

    return successfulMsgForLearnerSubmission[i];
  };

  const handleShowSubmitDiv = () => {
    if (
      Alert.status === constants.PROGRESS_STATUS.UNLOCKED ||
      Alert.status === constants.PROGRESS_STATUS.REATTEMPT ||
      Alert.status === constants.PROGRESS_STATUS.REATTEMPT_NEW_VERSION ||
      Alert.status === constants.PROGRESS_STATUS.QN_CONTINUE
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleShowCommentsButton = () => {
    if (
      Alert.comingObj.hasOwnProperty("commentsdata") ||
      Alert.testDetail.hasOwnProperty("commentsdata") ||
      Alert.codeDetails.hasOwnProperty("commentsdata") ||
      Alert.testData.hasOwnProperty("commentsdata")
    ) {
      Alert.setCommentsData(
        Alert.comingObj.commentsdata ||
        Alert.testDetail.commentsdata ||
        Alert.codeDetails.commentsdata ||
        Alert.testData.commentsdata
      );

      return true;
    } else {
      return false;
    }
  };

  const handleShowScore = () => {
    if (Alert.testData.viewtest === constants.DISPLAY_TEST_RESULT.ALL) {
      return true;
    } else {
      return false;
    }
  };
  const { startTest } = StudentTestPageHooks();

  const handleNetworkError = (from) => {
    Alert.setResultCode(4000);

    let newShowNotify = {
      show: true,
    };

    if (from === "Submit" && Alert.showNotify.primaryButton !== "Save") {
      newShowNotify.primaryButton = from;
    } else {
      newShowNotify.primaryButton = "Save";
    }

    newShowNotify.title = "Warning";
    newShowNotify.primaryButtonFunction =
      Alert.testData.testtype === constants.libTypeIds.CODING_CHALLENGE
        ? handleSaveTest
        : submitAnswers;
    newShowNotify.msg =
      "There appears to be an issue with your network. Please exit fullscreen mode, check your network, and then click the save button to save your answer.";

    Alert.setShowNotify(newShowNotify);
  };

  const handleToGetLearnerTimeTakenToSubmit = () => {
    let secs = Alert.codeDetails.timelimit * 60 - Alert.timeLimit;
    let minutes = Math.floor(secs / 60);

    secs = secs % 60;

    let hours = Math.floor(minutes / 60);

    minutes = minutes % 60;

    return pad(hours) + " : " + pad(minutes) + " : " + pad(secs);
  };

  const onFocus = () => {
    isBlurHandled.current = false; // Reset on focus
    focusOutSec.current = 0;
    clearInterval(focusOutTimer.current);
    window.removeEventListener("blur", onBlur);
  };

  const onBlur = () => {

    if (isBlurHandled.current) return;
    isBlurHandled.current = true;

    Alert.tabSwitchCount.current = Alert.tabSwitchCount.current + 1;

    focusOutTimer.current = setInterval(() => {
      let second = focusOutSec.current;

      if (second == 10 || Alert.tabSwitchCount.current == 3) {
        clearInterval(focusOutTimer.current);
        Alert.isLearnerQuitQns.current = true;
        handleSubmitLearnerCodeEvaluation(true, true, true, false);
      } else {
        second++;
        focusOutSec.current = second;
      }
    }, 1000);
  };

  const handleSubmit = () => {
    const questions = alertRef.current.testDetail.data.sections[0]?.questions || [];
    const answeredCount = questions.filter(q => q.solution && q.solution.trim() !== "").length;
    const progress = (answeredCount / questions.length) * 100; // 50%

    Alert.setShowNotify({
      show: true,
      titleSvg: "info",
      primaryButton: "Confirm",
      title: "Confirm Test Submission",
      secondaryButton: "Cancel",
      msg: "Are you sure you want to submit? Please ensure you have answered all questions. Once submitted, your responses cannot be changed and will be evaluated.",
      sql: true,
      total: questions.length,
      completed: answeredCount,
      percentage: progress,
      primaryButtonFunction: () => submitAnswers(),
    });
  };

  const handleQuitTest = () => {
    Alert.setLpData({});
    handleClose();
    Alert.isLearnerQuitQns.current = true;
    Alert.tabSwitchCount.current = 0;

    /* TODO: Zoom Integration */

    Alert.leaveMeeting();
    handleSubmitLearnerCodeEvaluation(false, false, true, false);
  };

  const handleSaveTest = () => {
    handleClose();

    /* TODO: Zoom Integration */

    Alert.leaveMeeting();
    handleSubmitLearnerCodeEvaluation(false, false, false, false);
  };

  const handleTimeOut = () => {
    handleClose();
    Alert.setLpData({});

    /* TODO: Zoom Integration */

    Alert.leaveMeeting();
    handleSubmitLearnerCodeEvaluation(false, false, false, true);
  };

  const checkFullScreen = () => {
    Alert.isLearnerQuitQns.current = true;
    handleSubmitLearnerCodeEvaluation(true, true, true, false);
  };

  const handleNavigateToHome = () => {
    Alert.setIsTakeTest(false);
    Alert.setFromCodeEditor(true);
    Alert.setLpData({});
    Alert.setTestDetail({});
    Alert.setCurrentStep(0);
    Alert.setShowInstruction(false);
    Alert.setIsShowTimeAlert(false);
    Alert.setLocationAvailable(false);
    Alert.setProctorLocation(null);
    Alert.setLocationError(null);
    Alert.setTimeComplexityResult(null);
    handleClose();

    if (Alert.testData.proctored === constants.PROCTORING_MODE.DUAL) {
      updateStatus({
        status: 3,
      });
    }
    console.log("Alert.testData:", Alert.testData);
    console.log("Alert.codeDetails:", Alert.codeDetails);
    console.log("Alert.codeDetails.secData:", Alert.codeDetails.secData);
    if (
      Alert.testData.mockuse &&
      Alert.testData.testtype === constants.libTypeIds.CODING_CHALLENGE &&
      Alert.codeDetails.secData.completedcount + 1 <
      Alert.codeDetails.secData.totalCount
    ) {
      let test = {
        ...Alert.testData,
        lmtid: Alert.codeDetails.lmtid,
        lmpid: Alert.codeDetails.lmpid,
      };
      console.log("test for assign next qn:", test);
      Alert.navigate("/instruction");
      handleCodingInfo(test);
    } else if (Alert.testData.hasOwnProperty("lccid")) {
      console.log("into the navigate lccid");
      if(Alert.testData.random) Alert.navigate(`/main/cc`);
      else Alert.navigate(`/main/cc/section/${Alert.testData.secid}`);
    } else {
      console.log("into the navigate else");
      handleNavigate();
    }

    Alert.setResultCode("");
    Alert.tabSwitchCount.current = 0;

    /* TODO: Zoom Integration */

    Alert.leaveMeeting();
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    Alert.setAssignQnDetailsForLearner([]);
    removeAccess();
  };

  const getSaveAnsPayload = () => {
    let payload = {
      testid: alertRef.current.testData.testid,
      mockuse: alertRef.current.testData.mockuse,
      libtype: alertRef.current.testData.testtype,
      testmode: alertRef.current.testData?.testmode,
      lpconfigid: alertRef.current.testData.lpconfigid,
      learnerid: getDataFromStorage("learnerid"),
    };

    if (alertRef.current.testData.testtype === constants.libTypeIds.CODING_CHALLENGE) {
      payload.answer = alertRef.current.learnerCode;
      payload.timeleft = alertRef.current.timeLimit;
      payload.questionid = alertRef.current.codeDetails.qnid;
      payload.languageid = alertRef.current.selectedLanguage.value;
      if(alertRef.current.testData.lccid){
        payload.testid = alertRef.current.testData.lccid;
        payload.ccsection = true;
      }
    } else {
      let studentAnswers = [];

      if (alertRef.current.testData.testtype === constants.libTypeIds.SQL) {
        alertRef.current.testDetail.data.sections[0]?.questions.forEach((key) => {
          studentAnswers.push({
            qnid: key.id,
            answer: key.solution || "",
          });
        });
      } else {
        for (const key in alertRef.current.answerRef.current) {
          studentAnswers.push({
            qnid: key,
            answer: alertRef.current.answerRef.current[key],
          });
        }
      }

      if (
        alertRef.current.testData.testtype === constants.libTypeIds.MCQ ||
        alertRef.current.testData.testtype === constants.libTypeIds.SQL ||
        alertRef.current.testData.testtype === constants.libTypeIds.DESCRIPTIVE ||
        alertRef.current.testData.testtype === constants.libTypeIds.CODING_SNIPPET
      ) {
        payload.answers = studentAnswers;
        payload.timeleft = alertRef.current.duration.current ?? alertRef.current.timeLimit;
      }
    }
    return JSON.stringify(payload);
  };

  const saveCurrentAnswer = () => {
    axios.post(
      "node/learner/question/saveans",
      getSaveAnsPayload(),
      getHeaderDetailsForAxios()
    );
  };

  const handleSubmitLearnerCodeEvaluation = async (
    isAutoSubmit,
    isSwitched,
    isQuit,
    isTimeOut,
    actionType,
    multiScreen
  ) => {
    if (navigator.onLine) {
      Alert.isLearnerQuitQns.current = isQuit;

      let newCodeDetials = JSON.parse(JSON.stringify(Alert.codeDetails));

      let isUserCodeEMpty = true;
      if (Alert.isPracticeRetake) {
        // handling practice retake

        newCodeDetials.status = constants.PROGRESS_STATUS.REATTEMPT;
      }

      if (newCodeDetials.status === constants.QUESTION_STATUS.ASSIGNED) {
        let learnerNewCode = newCodeDetials.templatecode.filter(
          (codeDetail) => codeDetail.langid === Alert.selectedLanguage.value
        );

        if (learnerNewCode[0].userCode.trim().length === 0) {
          isUserCodeEMpty = false;
        }
      } else if (
        newCodeDetials.status === constants.QUESTION_STATUS.RESUBMIT ||
        newCodeDetials.status === constants.QUESTION_STATUS.REATTEMPT_NEW_VERSION ||
        newCodeDetials.status === constants.QUESTION_STATUS.CONTINUE
      ) {
        if (Alert.learnerCode.trim().length === 0) {
          isUserCodeEMpty = false;
        }
      }

      if (isUserCodeEMpty) {
        if (
          !isSwitched &&
          !isAutoSubmit &&
          !newCodeDetials.isChangeCode &&
          Alert.resultCode !== constants.RESULT_STATUS.NETWORK_ISSUE
        ) {
          Alert.setShowNotify({
            show: true,
            title: "Warning",
            titleSvg: "warning",
            msg: "We do not see any change in the code. Write your code and then submit.",
          });
        } else {
          let specs = {};
          let learnerNewCode =[]
            const templateCode = typeof newCodeDetials.templatecode === "string" ? JSON.parse(newCodeDetials.templatecode):newCodeDetials.templatecode
            learnerNewCode =  templateCode.filter(
                (codeDetail) =>
                  codeDetail.langid === Alert.selectedLanguage.value
              )

          specs.method = "post";
          specs.api = "submitcode";
          specs.time = handleToGetLearnerTimeTakenToSubmit();
          specs.language = Alert.selectedLanguage?.label.toLowerCase();
          console.log("Proctor location before submit:", Alert.proctorLocation);
          specs.body = {
            multiScreen,
            type: "submitcode",
            timedout: isTimeOut,
            switched: isSwitched,
            autoSubmit: isAutoSubmit,
            timeleft: Alert.timeLimit,
            proctored: Alert.camAvailable,
            lmtid: Alert.codeDetails.lmtid,
            lmpid: Alert.codeDetails.lmpid,
            lccid: Alert.codeDetails.lccid,
            mockuse: Alert.testData.mockuse,
            notebook: Alert.testData.notebook,
            questionid: Alert.codeDetails.qnid,
            quit: Alert.isLearnerQuitQns.current,
            mocklp: localStorage.getItem("mockLp"),
            languageid: Alert.selectedLanguage.value,
            matchqnCount: Alert.matchqnCount.current,
            lpconfigid: Alert.codeDetails.lpConfigId,
            learnerid: getDataFromStorage("learnerid"),
            switchedcount: Alert.tabSwitchCount.current,
            language: Alert.selectedLanguage.label.toLowerCase(),
            code:
              newCodeDetials.status === constants.QUESTION_STATUS.RESUBMIT ||
              newCodeDetials.status === constants.QUESTION_STATUS.REATTEMPT_NEW_VERSION ||
                newCodeDetials.status === constants.QUESTION_STATUS.CONTINUE
                ? Alert.learnerCode
                : learnerNewCode[0].userCode,
            testid:
              !Alert.codeDetails.hasOwnProperty("lccid") &&
              Alert.codeDetails.secData.testId,
            testmode:
              !Alert.codeDetails.hasOwnProperty("lccid") &&
              Alert.codeDetails.secData?.testMode,
            totalqncount:
              !Alert.codeDetails.hasOwnProperty("lccid") &&
              Alert.codeDetails.secData.totalCount,
            latitude: Alert.proctorLocation?.latitude || null,
            longitude: Alert.proctorLocation?.longitude || null,
            address: Alert.proctorLocation?.address || null,
          };
          // if(specs.body.testmode === constants.PROCTORING_MODE.TAB_CAM_LOC){
          //   specs.body.latitude = Alert.proctorLocation.latitude;
          //   specs.body.longitude = Alert.proctorLocation.longitude;
          //   specs.body.address = Alert.proctorLocation.address;
          // }
          try {
            Alert.setIsLoaded(true);

            let response = await apiCall(specs);
            Alert.setIsPracticeRetake(false); // handling practice retake
            console.log("Response for actiontype:", actionType);
            if (!actionType){
              console.log("Response after code submission 1:", response);
              handleResponseForSubmittedLearnerCodefunction(
                response,
                isAutoSubmit,
                isSwitched,
                isQuit,
                isTimeOut,
                multiScreen
              );
            }
            else {
              if (actionType === constants.TEST_ACTIONS.REMOVE_FROM_TEST) {
                Alert.leaveMeeting();
                handleNavigateToHome();
              } else if (
                actionType === constants.TEST_ACTIONS.REMOVE_FROM_BATCH
              ) {
                Alert.handleDisableUser();
                localStorage.removeItem("lpid");
                Alert.navigate("/main/landing");
                Alert.setTimeComplexityResult(null);
                localStorage.removeItem("batchid");
              }
            }
          } catch (error) {
            if (
              Alert.resultCode === constants.RESULT_STATUS.NETWORK_ISSUE &&
              error.message !== "Network Error"
            ) {
              Alert.leaveMeeting();
              handleNavigateToHome();
            } else {
              if (
                (error.message === "timeout of 30000ms exceeded" ||
                  error.message.includes("504")) &&
                navigator.onLine
              ) {
                Alert.setShowNotify({
                  show: true,
                  title: "Error",
                  titleSvg: "error",
                  secondaryButton: "Submit",
                  primaryButton: "Try Again",
                  secondaryButtonFunction: handleTimeOut,
                  msg: (
                    <NotifyBody component="codingPageNotifyMsgForInfiniteLoopWhileSubmitting" />
                  ),
                });
              } else if (
                !navigator.onLine ||
                error.message === "Network Error"
              ) {
                handleNetworkError("Submit");
              } else {
                getCatchBlockDetails(error);
              }
            }
          } finally {
            Alert.setIsLoaded(false);
          }
        }
      } else {
        Alert.setShowNotify({
          size: "sm",
          show: true,
          title: "Error",
          titleSvg: "error",
          msg: "There is no code to submit",
        });
      }
    } else {
      handleNetworkError("Submit");
    }
  };

  /* mcq */

  const submitAnswers = (auto, time, from, actionType, multiScreen) => {
    if (navigator.onLine) {
      setHasSubmitted(true);

      /* TODO: Zoom Integration */

      Alert.leaveMeeting();

      // console.log("Alerrt.test:", Alert.testDetail);
      // console.log("Alert.comingObj:", Alert.comingObj);
      // console.log("testData:", Alert.testData);

      let testTypeId =
        Alert.comingObj?.sections?.[0]?.qnconfig?.[0]?.libtypeid ?? Alert.testDetail.libtypeid;

      if (
        testTypeId === constants.libTypeIds.SAQ ||
        testTypeId === constants.libTypeIds.LAQ
      ) {
        testTypeId = constants.libTypeIds.DESCRIPTIVE;
      }

      if (
        testTypeId === constants.libTypeIds.MCQ_MULTI ||
        testTypeId === constants.libTypeIds.MCQ_SINGLE
      ) {
        testTypeId = constants.libTypeIds.MCQ;
      }

      clearInterval(Alert.interval.current);
      handleClose();

      let studentAnswers = [];
      let filteredAnswers;

      Alert.setIsLoaded(true);

      if (
        Alert.comingObj.videoqn === 1 &&
        Alert.videoQn.current !== undefined &&
        Alert.videoQn.current.length > 0
      ) {
        let { current: tempVideoQn } = Alert.videoQn;
        let { current: tempQnListObj } = Alert.questionListObj;

        tempQnListObj.forEach((qobj) => {
          Object.values(qobj.qnsObj).forEach((qnData) => {
            tempVideoQn.forEach((det) => {
              if (
                qnData.answer &&
                qnData.qId === det.id &&
                (!det.answer || det.answer === "")
              ) {
                det.answer = qnData.answer;
                det.transcript = qnData.transcript;
              }

              if (qnData.qId === det.id) {
                det.transcript = qnData.transcript;
              }
            });
          });
        });

        Alert.videoQn.current.forEach((key) => {
          studentAnswers.push({
            qnid: key.id,
            answer: key.answer || "",
          });
        });
      } else if (from === "interview") {
        if (Alert.comingObj.answer) {
          Alert.comingObj.answer.forEach((key) => {
            studentAnswers.push({
              qnid: key.qnid,
              report: key.report,
              answer: key.answer || "",
              transcript: key.transcript || "",
            });
          });
        }
      } else if (testTypeId === constants.libTypeIds.SQL) {
        if (Alert.testDetail.data.sections[0]?.questions) {
          console.log("adding answer", Alert.testDetail.data.sections[0]);
          Alert.testDetail.data.sections[0]?.questions.forEach((key) => {
            studentAnswers.push({
              qnid: key.id,
              answer: key.solution || "",
            });
          });
        }
      } else {
        for (const key in Alert.answerRef.current) {
          studentAnswers.push({
            qnid: key,
            answer: Alert.answerRef.current[key],
          });
        }
      }

      let isEMpty = studentAnswers.some((data) => !data.answer);

      if (
        Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
        (Alert.testData.testtype === constants.libTypeIds.MCQ ||
          // Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE||
          Alert.testData.testtype === constants.libTypeIds.CODING_SNIPPET) &&
        !isEMpty
      ) {
        Alert.PractSingleQnFinalSubmit.current = true;
      }

      if (
        Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
        (Alert.testData.testtype === constants.libTypeIds.MCQ ||
          // Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE ||
          Alert.testData.testtype === constants.libTypeIds.CODING_SNIPPET)
      ) {
        if (isEMpty || Alert.PractSingleQnId.current !== "") {
          filteredAnswers = studentAnswers.filter(
            (data) => data.qnid == Alert.PractSingleQnId.current
          );
        } else {
          filteredAnswers = studentAnswers;
        }
      }

      if (Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
        (Alert.testData.testtype === constants.libTypeIds.MCQ ||
          // Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE ||
          Alert.testData.testtype === constants.libTypeIds.CODING_SNIPPET) && !filteredAnswers[0].answer
      ) {
        Alert.setIsLoaded(false);
        Alert.setShowNotify({
          show: true,
          title: "Info",
          msg: "Please enter an answer before submitting this question.",
        });
      } else {
        const payload ={
          multiScreen,
              testtype: testTypeId,
              lmtid: Alert.comingObj.lmtid,
              lmpid: Alert.comingObj.lmpid,
              mockuse: Alert.testData.mockuse,
              testmode: Alert.comingObj.testmode,
              switchedcount: switchCount.current,
              mocklp: localStorage.getItem("mockLp"),
              matchqnCount: Alert.matchqnCount.current,
              timeleft: Alert.duration.current ?? Alert.timeLimit,
              learnerid: parseInt(getDataFromStorage("learnerid")),
              switched: auto === undefined || auto === null ? false : true,
              autosubmit: auto === undefined || auto === null ? false : true,
              autoeval:
                Alert.mcqAndDesTestAndLpId.autoEval || Alert.testData.autoeval,
              lpconfigid: testTypeId !== constants.libTypeIds.SQL ?
                Alert.mcqAndDesTestAndLpId.lpconfigid :
                Alert.testData.lpconfigid,
              testid: testTypeId !== constants.libTypeIds.SQL ?
                parseInt(Alert.mcqAndDesTestAndLpId.testid) :
                Alert.testDetail.data.testid,
              lpid:
                parseInt(Alert.mcqAndDesTestAndLpId.lpid) ||
                sessionStorage.getItem("lpId"),
              answers:
                Alert.comingObj.testmode !== constants.MODE.PRACTICE_MODE ||
                  (Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
                    Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE &&
                    Alert.testData.autoeval === 0) ||
                  testTypeId === constants.libTypeIds.SQL ||
                  from === "interview"
                  ? studentAnswers
                  : filteredAnswers?.length === studentAnswers?.length
                    ? studentAnswers
                    : filteredAnswers,
              proctored:
                Alert.mcqAndDesTestAndLpId.proctored ===
                constants.PROCTORING_MODE.TAB_CAM ||
                Alert.mcqAndDesTestAndLpId.proctored ===
                constants.PROCTORING_MODE.TAB_CAM_LOC ||
                Alert.testData.proctored === constants.PROCTORING_MODE.TAB_CAM ||
                Alert.testData.proctored === constants.PROCTORING_MODE.TAB_CAM_LOC,
              qnCount:
                Alert.comingObj.testmode !== constants.MODE.PRACTICE_MODE ||
                  (!Alert.testData.autoeval &&
                    Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
                    Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE)
                  ? null
                  : filteredAnswers?.length === studentAnswers?.length
                    ? null
                    : studentAnswers?.length,
              latitude: Alert.proctorLocation?.latitude || null,
              longitude: Alert.proctorLocation?.longitude || null,
              address: Alert.proctorLocation?.address || null,
        };
        // if(payload.testmode === constants.PROCTORING_MODE.TAB_CAM_LOC){
        //     payload.latitude = Alert.proctorLocation.latitude;
        //     payload.longitude = Alert.proctorLocation.longitude;
        //     payload.address = Alert.proctorLocation.address;
        //   }
        axios
          .post(
            "node/learner/course/test/submit",
            payload,
            {
              timeout: 30000,
              headers: {
                "Content-Type": "application/json",

              },
            }
          )
          .then((res) => {
            Alert.setIsLoaded(false);

            if (
              res.data.resultCode !== 4001 &&
              res.data.resultCode !== constants.RESULT_STATUS.SUCCESS
            ) {
              Alert.setShowNotify({
                show: true,
                title: "Info",
                msg: res.data.message,
              });

              /* TODO: Zoom Integration */
              Alert.leaveMeeting();
            } else {
              if (actionType === constants.TEST_ACTIONS.REMOVE_FROM_BATCH) {
                Alert.handleDisableUser();
                Alert.leaveMeeting();
                localStorage.removeItem("lpid");
                Alert.navigate("/main/landing");
                localStorage.removeItem("batchid");
              } else if (actionType === constants.TEST_ACTIONS.REMOVE_FROM_TEST) {
                handleNavigateToHome();
                Alert.leaveMeeting();
              }

              if (
                Alert.comingObj.testmode === constants.MODE.PRACTICE_MODE &&
                (Alert.testData.testtype === constants.libTypeIds.MCQ ||
                  // Alert.testData.testtype === constants.libTypeIds.DESCRIPTIVE ||
                  Alert.testData.testtype === constants.libTypeIds.CODING_SNIPPET)
              ) {
                Alert.setPractSingleQnAns(res.data.correctAns);
                Alert.PractSingleQnAns.current = true;
                Alert.setHalfDonutChart(res.data);
              } else if (
                !actionType ||
                actionType === constants.TEST_ACTIONS.WARN
              ) {
                if (time) {
                  /* TODO: Zoom Integration */

                  Alert.leaveMeeting();

                  if (Alert.qnTrack.current !== Alert.VideoSaved) {
                    Alert.setButtonDisable(true);
                    Alert.setShowNotify({
                      show: true,
                      title: "Time lapsed",
                      primaryButton: "Back to the learning path",
                      primaryButtonFunction: handleNavigateToHome,
                      msg: <NotifyBody component="videoTestLowNetwork" />,
                    });
                  } else {
                    Alert.setShowNotify({
                      show: true,
                      title: "Time lapsed",
                      primaryButton: "Back to the learning path",
                      primaryButtonFunction: handleNavigateToHome,
                      msg: (
                        <NotifyBody component="codingPageNotifyMsgForTimeLapsedsWhileSubmitting" />
                      ),
                    });
                  }
                } else if (auto) {
                  /* TODO: Zoom Integration */
                  Alert.leaveMeeting();
                  if (Alert.qnTrack.current !== Alert.VideoSaved) {
                    Alert.setButtonDisable(true);
                    Alert.setShowNotify({
                      show: true,
                      title: "Auto-submitted",
                      primaryButton: "Back to the learning path",
                      primaryButtonFunction: handleNavigateToHome,
                      msg: <NotifyBody component="videoTestLowNetwork" />,
                    });
                  } else {
                    Alert.setShowNotify({
                      show: true,
                      title: "Test Auto-submitted",
                      primaryButton: "Back to the learning path",
                      primaryButtonFunction: handleNavigateToHome,
                      msg: (
                        <NotifyBody
                          multiScreen={multiScreen}
                          component="codingPageNotifyMsgForTabSwitchesWhileSubmitting"
                        />
                      ),
                    });
                  }
                } else {
                  if (Alert.qnTrack.current !== Alert.VideoSaved) {
                    Alert.setButtonDisable(true);
                    Alert.setShowNotify({
                      show: true,
                      title: "success",
                      primaryButton: "Back to the learning path",
                      primaryButtonFunction:
                        Alert.testData?.testmode === constants.MODE.TEST_MODE
                          ? handleNavigateToHome
                          : handleShowAnsforPrac,
                      msg: <NotifyBody component="videoTestLowNetwork" />,
                    });
                  }
                  // start Test Mode with Target
                  else if (!res.data.target) {
                    console.log("res:", res);

                    console.log("Alertstatus:", Alert.status);

                    Alert.setHalfDonutChart(res.data);
                    setHasSubmitted(false);
                    Alert.setShowNotify({
                      size: "lg",
                      show: true,
                      primaryButton: "Retake Test",
                      secondaryButton: "Back to the learning path",
                      secondaryButtonFunction: handleNavigateToHome,
                      primaryButtonFunction: () => {
                        Alert.answerRef.current = {};
                        Alert.setShowNotify({ show: false });
                        startTest();
                      },
                      msg: (
                        <NotifyBody
                          isChart={true}
                          multiScreen={multiScreen}
                          targetPercent={res.data.targetPercent}
                          component="mcqTestModeWithTargetCriteriaFailed"
                          achievedpercent={Math.round(res.data.achievedpercent)}
                        />
                      ),
                    });
                  }
                  // end Test Mode with Target
                  else {
                    Alert.setShowNotify({
                      size: "sm",
                      show: true,
                      titleSvg: "success",
                      msg: (
                        <NotifyBody
                          component="mcqAndDescPageNotifyMsgForSuccessWhileSubmitting"
                          msg={
                            res.data.resultCode === 4001
                              ? "Test submitted successfully"
                              : res.data.message
                          }
                        />
                      ),
                      primaryButtonFunction:
                        // Alert.testData.testmode === constants.MODE.TEST_MODE?
                        handleNavigateToHome,
                      // : handleShowAnsforPrac,
                    });
                  }
                }
              }
            }
          })
          .catch((error) => {
            if (
              error.message === "timeout of 30000ms exceeded" ||
              (error.message.includes("504") && navigator.onLine)
            ) {
              Alert.setShowNotify({
                show: true,
                title: "Error",
                msg: "Something went wrong…. Please, try again",
              });
            } else if (!navigator.onLine || error.message === "Network Error") {
              handleNetworkError();
            } else {
              getCatchBlockDetails(error);
            }
          })
          .finally(() => Alert.setIsLoaded(false));

      }
    } else {
      handleNetworkError();
    }
  };

  const getNextTopic = (testData, topicId) => {
    const topics = testData?.data?.topics || [];

    const currentIndex = topics.findIndex((topic) => topic.topicid === topicId);

    if (currentIndex !== -1 && currentIndex < topics.length - 1) {
      Alert.setCodeDetails((prev) => ({
        ...prev,
        secData: topics[currentIndex + 1],
        topics: topics[currentIndex + 1],
      }));

      return topics[currentIndex + 1];
    }

    return null;
  };

  const handleResponseForSubmittedLearnerCodefunction = async (
    response,
    isAutoSubmit,
    isSwitched,
    isQuit,
    timedout,
    multiScreen
  ) => {
    if (Alert.resultCode === constants.RESULT_STATUS.NETWORK_ISSUE) {
      Alert.leaveMeeting();
      //save ans api calling timeout clearing
      Alert.setCount(0);
      handleNavigateToHome();
    } else {
      //save ans api calling timeout clearing
      Alert.setCount(0);
      
      Alert.setResultCode(response.resultCode);
      if (response.resultCode === constants.RESULT_STATUS.SUCCESS) {
        Alert.setIsTakeTest(false);

        localStorage.removeItem("splitPos");
        localStorage.removeItem("splitWidthPos");
        console.log("Code Details:", JSON.parse(JSON.stringify(Alert.codeDetails)));
        // let newCodeDetails = JSON.parse(JSON.stringify(Alert.codeDetails));
        if (Alert.codeDetails?.secData !== undefined) {
          let newCodeDetails = JSON.parse(JSON.stringify(Alert.codeDetails));

          if (
            !newCodeDetails.hasOwnProperty("lccid") &&
            newCodeDetails?.secData?.completedcount
          ) {
            newCodeDetails.secData.completedcount =
              newCodeDetails.secData?.completedcount + 1;
          }

          let completedcount =
            newCodeDetails?.secData?.completedcount === 0
              ? newCodeDetails?.secData?.completedcount + 1
              : newCodeDetails?.secData?.completedcount;

          Alert.codeDetails.secData.completedcount = completedcount;

          if (completedcount === newCodeDetails?.secData?.totalcount) {
            let data = await getNextTopic(
              Alert.testData,
              newCodeDetails.secData.topicid
            );

            if (data !== null) {
              newCodeDetails.topics = data;
              newCodeDetails.secData = data;
              Alert.codeDetails.topics = data;
              Alert.codeDetails.secData = data;
              completedcount = data.completedcount;
              Alert.codeDetails.secData.totalCount = data.totalcount;
              Alert.codeDetails.secData.testId = Alert.testData.testid;
              Alert.codeDetails.secData.testMode = Alert.testData.testmode;
              Alert.codeDetails.secData.proctored = Alert.testData.proctored;
            }
          }

          Alert.setCodeDetails(newCodeDetails);
          console.log("Completed Count:", completedcount);
          console.log("Total Count:", newCodeDetails.secData.totalcount);
          if (
            !newCodeDetails.hasOwnProperty("lccid") &&
            completedcount < newCodeDetails.secData.totalcount
          ) {
            console.log("Entered into next question flow");
            Alert.setCount(0);
            Alert.setShowNotify({
              show: true,
              titleSvg: "success",
              primaryButton: "Go to next coding challenge",
              secondaryButton: "Back to the learning path",
              secondaryButtonFunction: handleNavigateToHome,
              primaryButtonFunction: handleAssignNextQnToLearner,
              msg: (
                <NotifyBody
                  timedOut={timedout}
                  codeDetails={newCodeDetails}
                  explanation={response.explanation}
                  targetPercent={response.targetPercent}
                  achievedpercent={Math.round(response.achievedpercent)}
                  component="codingPageNotifyMsgForSuccessWhileSubmitting"
                  isChart={
                    Alert.codeDetails.secData?.testMode ===
                    constants.MODE.PRACTICE_MODE ||
                    Alert.codeDetails.secData?.testMode ===
                    constants.MODE.TEST_MODE
                  }
                />
              ),
            });
          } else {
            console.log("Test completed. No more questions in this topic.");
            Alert.setIsShowTimeAlert(true);
            Alert.setShowNotify({
              show: true,
              titleSvg: "success",
              primaryButton: "Back to the learning path",
              primaryButtonFunction: handleNavigateToHome,
              msg: (
                <NotifyBody
                  timedOut={timedout}
                  codeDetails={newCodeDetails}
                  explanation={response.explanation}
                  notebook={Alert.testData.notebook}
                  targetPercent={response.targetPercent}
                  achievedpercent={Math.round(response.achievedpercent)}
                  component="codingPageNotifyMsgForSuccessWhileSubmitting"
                  isChart={
                    Alert.codeDetails.secData?.testMode ===
                    constants.MODE.PRACTICE_MODE ||
                    Alert.codeDetails.secData?.testMode ===
                    constants.MODE.TEST_MODE
                  }
                />
              ),
            });
          }
        } else {
          Alert.setShowNotify({
            show: true,
            title: "Success",
            msg: response.data,
            primaryButton: "Back to the learning path",
            primaryButtonFunction: handleNavigateToHome,
          });
        }
      }
      else if (
        response.resultCode === constants.RESULT_STATUS.REPLACE_QUESTION ||
        isQuit
      ) {
        localStorage.removeItem("splitPos");
        localStorage.removeItem("splitWidthPos");

        if (isAutoSubmit && isSwitched && isQuit) {
          Alert.setIsTakeTest(false);
          Alert.setIsShowTimeAlert(true);
          Alert.setShowNotify({
            show: true,
            title: "Auto-submitted",
            primaryButton: "Back to the learning path",
            primaryButtonFunction: handleNavigateToHome,
            msg: (
              <NotifyBody
                multiScreen={multiScreen}
                component="codingPageNotifyMsgForTabSwitchesWhileSubmitting"
              />
            ),
          });
        } else if (isAutoSubmit && !isSwitched && isQuit) {
          Alert.setIsTakeTest(false);
          Alert.setIsShowTimeAlert(true);
          Alert.setShowNotify({
            show: true,
            title: "Time lapsed",
            primaryButton: "Back to the learning path",
            primaryButtonFunction: handleNavigateToHome,
            msg: (
              <NotifyBody component="codingPageNotifyMsgForTimeLapsedsWhileSubmitting" />
            ),
          });
        } else if (isQuit) {
          Alert.leaveMeeting();
          handleNavigateToHome();
        } else {
          Alert.setShowNotify({
            show: true,
            title: "Info",
            msg: response.data,
          });
        }
      } else if (
        response.resultCode === constants.RESULT_STATUS.INCORRECT_ANSWER &&
        !isQuit
      ) {
        if (response.hasOwnProperty("target") && !response.target) {
          Alert.setShowNotify({
            show: true,
            size: "lg",
            secondaryButton: "Submit",
            title: "We found an issue",
            primaryButton: "Try Again",
            secondaryButtonFunction: () => {
              handleSubmitLearnerCodeEvaluation(false, false, true, false);
            },
            msg: (
              <NotifyBody
                target={false}
                explanation={response.explanation}
                targetPercent={response.targetPercent}
                achievedpercent={Math.round(response.achievedpercent)}
                component="codingPageNotifyMsgForFailedTestCasesStaticWhileSubmitting"
                isChart={
                  Alert.codeDetails.secData?.testMode ===
                  constants.MODE.PRACTICE_MODE ||
                  Alert.codeDetails.secData?.testMode ===
                  constants.MODE.TEST_MODE
                }
              />
            ),
          });
        } else {
          Alert.setShowNotify({
            show: true,
            secondaryButton: "Submit",
            title: "We found an issue",
            primaryButton: "Try Again",
            secondaryButtonFunction: handleQuitTest,
            msg: (
              <NotifyBody
                target={response.target}
                component="codingPageNotifyMsgForFailedTestCasesStaticWhileSubmitting"
              />
            ),
          });
        }
      } else if (
        response.resultCode === constants.RESULT_STATUS.TECHNICAL_ERROR
      ) {
        Alert.setShowNotify({
          show: true,
          title: "Error",
          msg: response.msg,
        });
      } else if (response.message) {
        Alert.setShowNotify({
          show: true,
          title: "Error",
          msg: response.message,
        });
      } else {
        Alert.setShowNotify({
          show: true,
          msg: response,
          title: "Error",
        });
      }
    }
  };

  return {
    timer,
    onBlur,
    onFocus,
    setTimer,
    timeoutId,
    focusOutSec,
    switchCount,
    hasSubmitted,
    handleSubmit,
    getSuccessMsg,
    handleTimeOut,
    submitAnswers,
    handleSaveTest,
    handleQuitTest,
    setHasSubmitted,
    checkFullScreen,
    handleShowScore,
    saveCurrentAnswer,
    handleNetworkError,
    handleShowSubmitDiv,
    handleNavigateToHome,
    handleShowCommentsButton,
    handleSubmitLearnerCodeEvaluation,
  };
}
